import {Alert, Intent} from "@blueprintjs/core";
import log from "loglevel";
import styled from "@emotion/styled";
import React, {JSXElementConstructor, useContext, useEffect, useMemo, useState} from "react";
import ReactDOM from "react-dom";
import {AppContext} from "../../ApplicationContext";

export enum MAP_PORTAL_ALERT {
  WHEN_OPEN = "The popped out map will work as long as the Dispatch tab that contains the job & driver board remains the primary tab on that browser window. Updates to the map will be paused if you select a different tab on that browser's window, which removes the Dispatch tab from view.",
  WHEN_PAUSE = "Map functionality is paused when the primary dispatch tab is not the selected tab on the browser's window. Or, if the map window is put to full screen mode covering the primary dispatch tab. Please adjust and it will work again."
}

interface NewWindowPortalProps {
  children: React.ReactNode;
  target?: string;
  title?: string;
  alertTextWhenOpen?: string;
  alertTextWhenPaused?: string;
  onClose?: () => void;
}
export const NewWindowPortalCommon: React.FC<NewWindowPortalProps> = ({
  children,
  target,
  title,
  alertTextWhenOpen,
  alertTextWhenPaused,
  onClose
}) => {
  const appContext = useContext(AppContext);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [usePortal, setUsePortal] = useState(false);

  const [isMinimizedAlertOpen, setIsMinimizedAlertOpen] = useState(false);

  const closeAlert = () => setIsAlertOpen(false);
  const newWindow = useMemo(() => {
    try {
      const windowOptions = "width=600,height=400,left=200,top=200";
      const openedWindow = window.open("", `${target ?? ""}`, windowOptions) ?? undefined;

      if (openedWindow) {
        setUsePortal(true);
        openedWindow.document.title = `${title ?? location.origin}`;
        openedWindow.addEventListener("beforeunload", () => {
          setUsePortal(false);
          if (onClose) {
            onClose();
          } else {
            appContext.dispatch({
              type: "SetOpenMapPortal",
              payload: false
            });
          }
        });
      }
      return openedWindow;
    } catch (error) {
      log.error("Error opening new window:", error);
      return undefined;
    }
  }, [appContext, onClose, target, title]);

  useEffect(() => {
    if (alertTextWhenOpen) {
      setIsAlertOpen(true);
    }
    const handleCloseNewWindow = () => {
      if (newWindow && !newWindow.closed) {
        newWindow.close();
      }
    };

    const handleVisibilityChanged = () => {
      setIsMinimizedAlertOpen(document.visibilityState === "hidden");
    };

    window.addEventListener("beforeunload", handleCloseNewWindow);
    document.addEventListener("visibilitychange", handleVisibilityChanged);
    return () => {
      window.removeEventListener("beforeunload", handleCloseNewWindow);
      document.removeEventListener("visibilitychange", handleVisibilityChanged);
    };
  }, [newWindow, alertTextWhenOpen]);

  if (children && newWindow) {
    // Ensure children is a valid React element
    copyStyles(window.document, newWindow.document);
    const validChildren = React.Children.only(children) as React.ReactElement<any, string | JSXElementConstructor<any>>;
    return (
      <>
        {ReactDOM.createPortal(React.cloneElement(validChildren, {newWindow, usePortal}), newWindow.document.body)}
        <StyledAlert
          icon={"info-sign"}
          isOpen={isAlertOpen}
          intent={Intent.PRIMARY}
          onClose={closeAlert}
          canOutsideClickCancel={false}
          confirmButtonText="Ok"
          portalContainer={newWindow.document.body}
        >
          <AlertContent>{alertTextWhenOpen}</AlertContent>
        </StyledAlert>
        <StyledAlert
          icon={"pause"}
          isOpen={isMinimizedAlertOpen}
          canOutsideClickCancel={false}
          intent={Intent.WARNING}
          portalContainer={newWindow.document.body}
          disableOkButton={true}
        >
          <AlertContent>{alertTextWhenPaused}</AlertContent>
        </StyledAlert>
      </>
    );
  }

  return null;
};

export function copyStyles(sourceDoc: Document, targetDoc: Document): void {
  Array.from(sourceDoc.styleSheets).forEach((styleSheet) => {
    try {
      if (styleSheet instanceof CSSStyleSheet) {
        const newStyleEl = sourceDoc.createElement("style");

        Array.from(styleSheet.cssRules).forEach((cssRule) => {
          newStyleEl.appendChild(sourceDoc.createTextNode(cssRule.cssText));
        });

        targetDoc.head.appendChild(newStyleEl);
      } else if ("href" in styleSheet) {
        const newLinkEl = sourceDoc.createElement("link");

        newLinkEl.rel = "stylesheet";
        newLinkEl.href = (styleSheet as any).href;
        targetDoc.head.appendChild(newLinkEl);
      }
    } catch (error) {
      // Error handling is intentionally empty as we are not logging errors.
      // This is to avoid cluttering the console with expected errors.
    }
  });
}
const StyledAlert = styled(Alert)<{disableOkButton?: boolean}>`
  .bp5-button.bp5-intent-primary {
    display: ${(props) => (props.disableOkButton ? "none" : "block")};
  }
  .bp5-button.bp5-intent-warning {
    display: ${(props) => (props.disableOkButton ? "none" : "block")};
  }
`;
const AlertContent = styled.span`
  font-size: 14px;
  font-family: Roboto, sans-serif;
`;
